/**
 * API Service — updated with column preview/confirm endpoints.
 */

import axios, { AxiosInstance } from 'axios';
import {
  UploadResponse,
  SheetInfo,
  ColumnPreviewResponse,
  ConfirmColumnsResponse,
  RowRangesBySheet,
  VerifyStartRequest,
  VerifyStartResponse,
  JobStatusResponse,
  AnalysisResponse,
  AllCallsResponse,
  BillingPlan,
  SubscriptionInfo,
  CckCreditsMe,
  CckCreditSummary,
  CckRedeemResult,
  CckAdminUser,
  CckCode,
  CckRedemptionHistory,
  CckLedgerEntry,
  GSheetInspectResponse,
  WritebackStatus,
  GSheetServiceAccount,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    console.error('API Error:', err.response?.data || err.message);
    return Promise.reject(err);
  }
);

// ── Upload ────────────────────────────────────────────────────────────────────

export const uploadApi = {
  async uploadFile(file: File): Promise<UploadResponse> {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post<UploadResponse>('/api/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  async getSheets(uploadId: string): Promise<SheetInfo[]> {
    const res = await api.get<SheetInfo[]>(`/api/upload/${uploadId}/sheets`);
    return res.data;
  },

  async previewColumns(
    uploadId: string,
    selectedSheets: string[],
    headerRows?: Record<string, number>
  ): Promise<ColumnPreviewResponse> {
    const res = await api.post<ColumnPreviewResponse>(
      `/api/upload/${uploadId}/preview-columns`,
      { selected_sheets: selectedSheets, header_rows: headerRows }
    );
    return res.data;
  },

  async confirmColumns(
    uploadId: string,
    selectedSheets: string[],
    columnMap: Record<string, string[]>,
    rowRanges?: RowRangesBySheet,
    headerRows?: Record<string, number>
  ): Promise<ConfirmColumnsResponse> {
    const res = await api.post<ConfirmColumnsResponse>(
      `/api/upload/${uploadId}/confirm-columns`,
      {
        selected_sheets: selectedSheets,
        column_map: columnMap,
        row_ranges: rowRanges,
        header_rows: headerRows,
      }
    );
    return res.data;
  },
};

// ── Google Sheets input (gsheet-io-01) ───────────────────────────────────────

export const gsheetsApi = {
  /** The account a customer must share their spreadsheet with, for the UI to display. */
  async serviceAccount(): Promise<GSheetServiceAccount> {
    const res = await api.get<GSheetServiceAccount>('/api/gsheets/service-account');
    return res.data;
  },

  /** Read-only look at a spreadsheet: its tabs, sizes and detected phone columns. */
  async inspect(url: string): Promise<GSheetInspectResponse> {
    const res = await api.post<GSheetInspectResponse>('/api/gsheets/inspect', { url });
    return res.data;
  },

  /**
   * Import the chosen tabs. Returns the same shape as a file upload, so the rest of
   * the flow (column preview, confirm, start) is identical from here on.
   */
  async importTabs(url: string, tabs: string[], headerRows?: Record<string, number>): Promise<UploadResponse> {
    const res = await api.post<UploadResponse>('/api/gsheets/import', { url, tabs, header_rows: headerRows });
    return res.data;
  },

  async writebackStatus(jobId: string): Promise<WritebackStatus> {
    const res = await api.get<WritebackStatus>(`/api/gsheets/job/${jobId}/writeback`);
    return res.data;
  },

  async retryWriteback(jobId: string): Promise<{ status: string; cells?: number }> {
    const res = await api.post(`/api/gsheets/job/${jobId}/writeback/retry`);
    return res.data;
  },
};

// ── Verification ──────────────────────────────────────────────────────────────

export const verifyApi = {
  async startVerification(request: VerifyStartRequest): Promise<VerifyStartResponse> {
    const res = await api.post<VerifyStartResponse>('/api/verify/start', request);
    return res.data;
  },

  async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    const res = await api.get<JobStatusResponse>(`/api/verify/${jobId}/status`);
    return res.data;
  },

  async getAnalysisStatus(jobId: string): Promise<AnalysisResponse> {
    const res = await api.get<AnalysisResponse>(`/api/jobs/${jobId}/analysis`);
    return res.data;
  },

  async rerunFailedAnalysis(jobId: string): Promise<{ queued: number; message: string; will_call_numbers: boolean }> {
    const res = await api.post(`/api/jobs/${jobId}/analysis/rerun-failed`);
    return res.data;
  },

  async getAllCalls(jobId: string): Promise<AllCallsResponse> {
    const res = await api.get<AllCallsResponse>(`/api/jobs/${jobId}/calls`);
    return res.data;
  },

  getRecordingUrl(jobId: string, callSid: string): string {
    return `${API_BASE_URL}/api/jobs/${jobId}/recordings/${callSid}`;
  },

  getFetchRecordingUrl(jobId: string, callSid: string): string {
    return `${API_BASE_URL}/api/jobs/${jobId}/calls/${callSid}/fetch-recording`;
  },

  async fetchRecordingOnDemand(jobId: string, callSid: string): Promise<string> {
    // Returns a blob URL ready for use in an <audio> element
    const res = await api.post(
      `/api/jobs/${jobId}/calls/${callSid}/fetch-recording`,
      {},
      { responseType: 'blob' }
    );
    return URL.createObjectURL(res.data);
  },

  async streamRecordingAsBlob(jobId: string, callSid: string): Promise<string> {
    // Authenticated GET of the already-downloaded recording, returned as a blob URL.
    // Required because the <audio> element cannot attach the Bearer token.
    const res = await api.get(
      `/api/jobs/${jobId}/recordings/${callSid}`,
      { responseType: 'blob' }
    );
    return URL.createObjectURL(res.data);
  },

  async getTranscript(jobId: string, callSid: string): Promise<any> {
    const res = await api.get<any>(`/api/jobs/${jobId}/calls/${callSid}/transcript`);
    return res.data;
  },

  async getJobResults(jobId: string): Promise<any> {
    const res = await api.get(`/api/verify/${jobId}/results`);
    return res.data;
  },

  async cancelJob(jobId: string): Promise<void> {
    await api.post(`/api/verify/${jobId}/cancel`);
  },
};

// ── Download ──────────────────────────────────────────────────────────────────

export const downloadApi = {
  getDownloadUrl(jobId: string): string {
    return `${API_BASE_URL}/api/download/${jobId}`;
  },

  async getDownloadInfo(jobId: string): Promise<any> {
    const res = await api.get(`/api/download/${jobId}/info`);
    return res.data;
  },

  async triggerDownload(jobId: string, filename: string): Promise<void> {
    const url = `${API_BASE_URL}/api/download/${jobId}`;
    const token = localStorage.getItem('auth_token');
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      let msg = `Download failed (HTTP ${res.status})`;
      try { msg = JSON.parse(body).detail ?? msg; } catch { /* ignore */ }
      alert(msg);
      return;
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  },
};

// ── Status / History ──────────────────────────────────────────────────────────

export const statusApi = {
  async listJobs(userId?: string): Promise<{ jobs: any[] }> {
    const res = await api.get('/api/jobs', { params: userId ? { user_id: userId } : undefined });
    return res.data;
  },

  async getHistory(userId?: string): Promise<{ history: any[] }> {
    const res = await api.get('/api/history', { params: userId ? { user_id: userId } : undefined });
    return res.data;
  },

  async getDashboard(): Promise<any> {
    const res = await api.get('/api/dashboard');
    return res.data;
  },

  async getJobBreakdown(jobId: string): Promise<any> {
    const res = await api.get(`/api/jobs/${jobId}/breakdown`);
    return res.data;
  },
};

// ── Billing (billing-credits-01) ────────────────────────────────────────────

export const billingApi = {
  /** Public — the 3 tiers for the pricing page (no auth, no secrets). */
  async getPlans(): Promise<BillingPlan[]> {
    const res = await api.get<{ plans: BillingPlan[] }>('/api/billing/plans');
    return res.data.plans;
  },

  /** Authed — current plan + usage (clear shape even with no subscription). */
  async getSubscription(): Promise<SubscriptionInfo> {
    const res = await api.get<SubscriptionInfo>('/api/billing/subscription');
    return res.data;
  },

  /** Authed — start hosted Checkout for a tier; returns the URL to redirect to. */
  async createCheckout(tier: string): Promise<string> {
    const res = await api.post<{ url: string }>('/api/billing/checkout', { tier });
    return res.data.url;
  },

  /** Authed — open the Customer Portal; returns the URL to redirect to. */
  async openPortal(): Promise<string> {
    const res = await api.post<{ url: string }>('/api/billing/portal', {});
    return res.data.url;
  },
};

// ── CCK free-credit / coupon system (cck-credits-01) ─────────────────────────

export const cckCreditsApi = {
  /** Current user's credit summary (zeroed shape with enabled=false when dark). */
  async getMe(): Promise<CckCreditsMe> {
    const res = await api.get<CckCreditsMe>('/api/credits/me');
    return res.data;
  },

  /** Combined CCK + Stripe balance — the single source every credit bar reads. */
  async getSummary(): Promise<CckCreditSummary> {
    const res = await api.get<CckCreditSummary>('/api/credits/summary');
    return res.data;
  },

  /** Redeem a CCK coupon code for free credits (one per user, ever). */
  async redeem(code: string): Promise<CckRedeemResult> {
    const res = await api.post<CckRedeemResult>('/api/credits/redeem', { code });
    return res.data;
  },

  /** Current user's credit-movement log (grants / reserves / refunds / expiry). */
  async getHistory(): Promise<{ enabled: boolean; entries: CckLedgerEntry[] }> {
    const res = await api.get('/api/credits/me/history');
    return res.data;
  },

  // ── Admin ──────────────────────────────────────────────────────────────────
  async adminListUsers(q?: string): Promise<{ users: CckAdminUser[]; total: number }> {
    const res = await api.get('/api/admin/credits/users', { params: q ? { q } : undefined });
    return res.data;
  },

  async adminGrant(
    userId: string,
    amount: number,
    note?: string,
    expiresAt?: string
  ): Promise<{ balance: number; granted: number; expires_at: string | null }> {
    const res = await api.post(`/api/admin/credits/users/${userId}/grant`, {
      amount, note: note || undefined, expires_at: expiresAt || undefined,
    });
    return res.data;
  },

  async adminListCodes(): Promise<{ codes: CckCode[]; redemptions: CckRedemptionHistory[] }> {
    const res = await api.get('/api/admin/credits/codes');
    return res.data;
  },

  async adminCreateCode(body: {
    code: string; credit_value: number; validity_days?: number;
    max_redemptions?: number | null; description?: string;
  }): Promise<CckCode> {
    const res = await api.post('/api/admin/credits/codes', body);
    return res.data;
  },

  async adminSetCodeActive(codeId: string, active: boolean): Promise<CckCode> {
    const res = await api.patch(`/api/admin/credits/codes/${codeId}`, { active });
    return res.data;
  },
};

export const healthCheck = async () => {
  const res = await api.get('/health');
  return res.data;
};

export default api;
